import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Progress, Select, Spin, Tag, Typography, message } from "antd";
import { useParams } from "react-router-dom";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import { completeRetouchUpload, createRetouchUploadBatch, fetchRetouchReviewInfo, getRetouchReview, getRetouchUploadReport, syncRetouchReview, syncRetouchReviewPublic, uploadRetouchFile } from "../../utils/retouchReviewApi";
import { formatRetouchVersion, getRetouchReviewCopy } from "../../utils/retouchReviewCopy";
import { alignRetouchUploadPaths, findRetouchFilenameMismatches, parseRetouchUploadFiles, RETOUCH_UPLOAD_ACCEPT, RETOUCH_SINGLE_UPLOAD_ACCEPT, fileNameFromPath, isJpgRetouchFile } from "../../utils/retouchReviewUpload";
import { useTranslation } from "../../i18n";
import "./retouchReview.css";

function Report({ report, copy }) {
    if (!report) return null;
    const versions = Object.entries(report.versionCounts || {});
    return <div className="retouch-upload-report"><div className="retouch-review-summary"><Tag>{copy.originalCount}: {report.originalCount}</Tag><Tag color="green">{copy.matched}: {report.matchedCount}</Tag><Tag color={report.extras?.length ? "orange" : "default"}>{copy.extra}: {report.extras?.length || 0}</Tag><Tag color={report.invalid?.length ? "red" : "default"}>{copy.invalid}: {report.invalid?.length || 0}</Tag></div>{versions.map(([version, value]) => <div key={version} style={{ marginBottom: 12 }}><Typography.Text strong>{formatRetouchVersion(version, copy)}: {value.matched}/{value.total}</Typography.Text>{value.missing?.length > 0 && <><Typography.Text type="danger"> · {copy.missing}</Typography.Text><ul>{value.missing.map((item) => <li key={item}>{item}</li>)}</ul></>}{value.extra?.length > 0 && <><Typography.Text type="warning"> · {copy.extra}</Typography.Text><ul>{value.extra.map((item) => <li key={item}>{item}</li>)}</ul></>}</div>)}</div>;
}

function RetouchReviewUpload({ reviewId: reviewIdProp, token: tokenProp, batchId: batchIdProp }) {
    const { locale } = useTranslation();
    const routeParams = useParams();
    const reviewId = reviewIdProp || routeParams.reviewId || "";
    const token = tokenProp || routeParams.token || "";
    const batchId = batchIdProp || routeParams.batchId || "new";
    const copy = getRetouchReviewCopy(locale);
    const [id, setId] = useState(reviewId || "");
    const [review, setReview] = useState(null);
    const [files, setFiles] = useState([]);
    const [batch, setBatch] = useState(batchId !== "new" ? batchId : "");
    const [report, setReport] = useState(null);
    const [running, setRunning] = useState(false);
    const [loading, setLoading] = useState(Boolean(token || reviewId));
    const [bulkVersion, setBulkVersion] = useState("latest");
    const [singlePhotoId] = useState(() => new URLSearchParams(window.location.search).get("photoId") || "");
    const [pendingSingleMismatch, setPendingSingleMismatch] = useState(null);
    const [batchFilenameMismatch, setBatchFilenameMismatch] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        let active = true;
        if (!token && !reviewId) {
            setLoading(false);
            return undefined;
        }
        const loadReview = token ? fetchRetouchReviewInfo(token) : getRetouchReview(reviewId);
        loadReview.then((value) => {
            if (!active) return;
            setReview(value);
            setId(value?.id || reviewId);
        }).catch(() => {}).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [reviewId, token]);

    useEffect(() => {
        if (batch && batch !== "new") getRetouchUploadReport({ batchId: batch, token }).then((data) => setReport(data.report)).catch(() => {});
    }, [batch, token]);

    const availableVersions = useMemo(() => {
        const discovered = review?.versions || (review?.photos || []).flatMap((photo) => Object.keys(photo.versions || {}));
        const numeric = Array.from({ length: 20 }, (_, index) => `v${index + 1}`);
        return [...new Set([...numeric, ...discovered])].filter((version) => /^v[1-9]\d*$/.test(version)).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
    }, [review]);
    const canUploadOriginal = !token || review?.role === "photographer";
    useEffect(() => {
        if (canUploadOriginal && !files.length) setBulkVersion("original");
    }, [canUploadOriginal, files.length]);
    const versionOptions = useMemo(() => [
        ...(canUploadOriginal ? [{ value: "original", label: copy.original }] : []),
        ...availableVersions.map((version) => ({ value: version, label: formatRetouchVersion(version, copy) })),
        { value: "latest", label: copy.latestVersion },
    ], [availableVersions, canUploadOriginal, copy]);
    const singlePhoto = review?.photos?.find((photo) => photo.id === singlePhotoId) || null;
    const singleUpload = Boolean(singlePhotoId);

    const selectedCount = files.length;
    const choose = (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        if (singleUpload) {
            if (selectedFiles.length > 1) {
                setFiles([]);
                message.error(copy.singleFileOnly);
                event.target.value = "";
                return;
            }
            if (selectedFiles[0] && !isJpgRetouchFile(selectedFiles[0])) {
                setFiles([]);
                message.error(copy.singleFileFormatError);
                event.target.value = "";
                return;
            }
        }
        const { files: parsedFiles, unsupported } = parseRetouchUploadFiles(selectedFiles, { forcedRelativePath: singlePhoto?.relativePath || "" });
        const parsed = singleUpload
            ? parsedFiles
            : alignRetouchUploadPaths(parsedFiles, review?.photos).map((item) => ({ ...item, version: bulkVersion }));
        setReport(null);
        if (unsupported.length) {
            setFiles([]);
            message.error(copy.jpgOnlyFormatError.replace("{files}", unsupported.map((file) => file.name).join(", ")));
            event.target.value = "";
            return;
        }
        if (!parsed.length) {
            setFiles([]);
            message.warning(copy.noFiles);
            event.target.value = "";
            return;
        }
        if (singleUpload && singlePhoto) {
            const selectedName = selectedFiles[0]?.name || "";
            const expectedName = fileNameFromPath(singlePhoto.relativePath);
            if (selectedName !== expectedName) {
                setFiles([]);
                setPendingSingleMismatch({ files: parsed, selectedName, expectedName });
                return;
            }
        }
        if (!singleUpload && review?.photos?.length && !canUploadOriginal) {
            const mismatches = findRetouchFilenameMismatches(parsed, review.photos);
            if (mismatches.length) {
                setFiles([]);
                setBatchFilenameMismatch(mismatches);
                event.target.value = "";
                return;
            }
        }
        setFiles(parsed);
    };

    const confirmSingleMismatch = () => {
        if (!pendingSingleMismatch) return;
        // The browser File object keeps its local name, but the manifest path is
        // deliberately replaced with the current photo's canonical path. The
        // Worker then stores it under the next per-photo version directory.
        setFiles(pendingSingleMismatch.files);
        setPendingSingleMismatch(null);
    };

    const cancelSingleMismatch = () => {
        setPendingSingleMismatch(null);
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };
    const updateVersion = (index, version) => {
        setFiles((prev) => prev.map((item, current) => current === index ? { ...item, version, resolvedVersion: "" } : item));
    };
    const applyVersion = (version) => {
        setBulkVersion(version);
        setFiles((prev) => prev.map((item) => ({ ...item, version, resolvedVersion: "" })));
    };
    const start = async () => {
        if (!id || !files.length) return;
        if (!singleUpload && review?.photos?.length) {
            const filesThatNeedAnOriginal = files.filter((item) => !canUploadOriginal || item.version !== "original");
            const mismatches = findRetouchFilenameMismatches(filesThatNeedAnOriginal, review.photos);
            if (mismatches.length) {
                setBatchFilenameMismatch(mismatches);
                return;
            }
        }
        setRunning(true);
        try {
            const created = await createRetouchUploadBatch({ id, token, mode: singleUpload ? "single" : "batch", files });
            const resolvedFiles = Array.isArray(created.files) ? created.files : [];
            const prepared = files.map((item, index) => ({
                ...item,
                resolvedVersion: resolvedFiles[index]?.version || item.version,
            }));
            setFiles(prepared);
            setBatch(created.batchId);
            let cursor = 0;
            const worker = async () => {
                while (cursor < prepared.length) {
                    const index = cursor++;
                    const item = prepared[index];
                    const uploadVersion = item.resolvedVersion || item.version;
                    setFiles((prev) => prev.map((value, current) => current === index ? { ...value, status: "uploading" } : value));
                    try {
                        await uploadRetouchFile({ batchId: created.batchId, token, file: item.file, version: uploadVersion, relativePath: item.relativePath, onProgress: (progress) => setFiles((prev) => prev.map((value, current) => current === index ? { ...value, progress } : value)) });
                        setFiles((prev) => prev.map((value, current) => current === index ? { ...value, status: "uploaded", progress: 1 } : value));
                    } catch (error) { setFiles((prev) => prev.map((value, current) => current === index ? { ...value, status: "failed" } : value)); }
                }
            };
            await Promise.all([worker(), worker(), worker()]);
            const completed = await completeRetouchUpload({ batchId: created.batchId, token });
            setReport(completed.report);
            if (completed.status !== "completed") {
                message.warning(copy.missing);
            } else {
                message.success(copy.uploadDone);
                try {
                    if (token) await syncRetouchReviewPublic({ id, token }); else await syncRetouchReview(id);
                    message.success(copy.autoSyncDone);
                    window.location.hash = token ? `/r/${token}` : `/r/audit/${id}`;
                } catch (error) {
                    message.error(error.message || copy.loadFailed);
                }
            }
        } catch (error) { message.error(error.message || copy.loadFailed); } finally { setRunning(false); }
    };
    const confirmSync = async () => {
        try {
            if (token) await syncRetouchReviewPublic({ id, token }); else await syncRetouchReview(id);
            message.success(copy.confirmSync);
            window.location.hash = token ? `/r/${token}` : `/r/audit/${id}`;
        } catch (error) { message.error(error.message || copy.loadFailed); }
    };
    const statusLabel = useMemo(() => ({ pending: copy.statusPending, uploading: copy.statusUploading, uploaded: copy.statusUploaded, failed: copy.statusFailed }), [copy.statusFailed, copy.statusPending, copy.statusUploaded, copy.statusUploading]);
    if (loading) return <><PageTitle><Navigation /></PageTitle><div className="retouch-review-loading"><Spin /></div><Footer /></>;
    return <><PageTitle><Navigation /></PageTitle><BackHomeIcon to={token ? `/r/${token}` : PANEL_PATH} /><main className="retouch-review-page"><header className="retouch-review-page__header"><h1>{singleUpload ? copy.uploadCurrentTitle : copy.uploadTitle}</h1><p>{singleUpload ? copy.chooseCurrentFile : copy.chooseFolder}</p></header><section className="retouch-review-panel"><div className="retouch-review-field"><label htmlFor="retouch-upload-files">{singleUpload ? copy.chooseSingleFile : copy.chooseFiles}</label><input ref={fileInputRef} id="retouch-upload-files" type="file" multiple={!singleUpload} accept={singleUpload ? RETOUCH_SINGLE_UPLOAD_ACCEPT : RETOUCH_UPLOAD_ACCEPT} onChange={choose} disabled={running} /><Typography.Paragraph className="retouch-review-hint">{singleUpload ? copy.singleUploadFormatHint : copy.batchUploadFormatHint}</Typography.Paragraph>{!singleUpload && <Typography.Paragraph className="retouch-review-hint">{copy.batchUploadFilenameHint}</Typography.Paragraph>}</div>{!singleUpload && <div className="retouch-upload-version-toolbar"><Typography.Text>{copy.selectVersion}</Typography.Text><Select value={bulkVersion} onChange={applyVersion} options={versionOptions} disabled={running || !files.length} style={{ minWidth: 220 }} /></div>}<Typography.Paragraph>{selectedCount ? copy.selectedFiles.replace("{count}", selectedCount) : copy.noFiles}</Typography.Paragraph>{files.length > 0 && <div>{files.map((item, index) => { const resolved = item.resolvedVersion && item.resolvedVersion !== item.version ? ` → ${formatRetouchVersion(item.resolvedVersion, copy)}` : ""; return <div className="retouch-upload-file" key={`${item.relativePath}-${index}`}><span className="retouch-upload-file__name" title={item.relativePath}>{item.relativePath}</span>{!singleUpload && <Select value={item.version} onChange={(value) => updateVersion(index, value)} options={versionOptions} disabled={running} style={{ width: 220 }} />}<Tag>{formatRetouchVersion(item.version, copy)}{resolved}</Tag><Tag>{statusLabel[item.status]}</Tag>{item.status === "uploading" && <Progress percent={Math.round(item.progress * 100)} size="small" style={{ width: 120 }} />}</div>; })}</div>}<div className="retouch-review-actions"><Button type="primary" onClick={start} loading={running} disabled={!files.length || !id}>{copy.startUpload}</Button>{report && <Button onClick={confirmSync}>{copy.confirmSync}</Button>}</div><Report report={report} copy={copy} /></section></main><Footer />{pendingSingleMismatch && <Modal open title={copy.singleFilenameMismatchTitle} onOk={confirmSingleMismatch} onCancel={cancelSingleMismatch} okText={copy.singleFilenameMismatchConfirm} cancelText={copy.cancel}><p>{copy.singleFilenameMismatch.replace("{selected}", pendingSingleMismatch.selectedName).replace("{expected}", pendingSingleMismatch.expectedName)}</p><p>{copy.singleFilenameMismatchHint}</p></Modal>}{batchFilenameMismatch && <Modal open title={copy.batchFilenameMismatchTitle} onOk={() => setBatchFilenameMismatch(null)} onCancel={() => setBatchFilenameMismatch(null)} okText={copy.understood} cancelText={copy.cancel}><p>{copy.batchFilenameMismatch}</p><ul>{batchFilenameMismatch.map((item) => <li key={`${item.relativePath}-${item.file.name}`}>{item.file.name} → {item.relativePath}</li>)}</ul></Modal>}</>;
}

export default RetouchReviewUpload;
